import moment from 'moment';
import BaseAPI from '../BaseAPI';
import { FileType, TimeStampedLine } from '../files/types';
import { IVFSResponse } from '../types';
/**
 * Upload API
 * Handles all file upload related operations
 */
class UploadAPI extends BaseAPI {
  /**
   * Initialize a multipart upload
   * @param params - Upload initialization parameters
   * @returns Promise with fileId and uploadId
   */
  async initializeMultipartUpload(params: {
    teamId: string;
    folderId: string;
    contentType: string;
  }): Promise<IVFSResponse<{ fileId: string; uploadId: string }>> {
    const { teamId, folderId, contentType } = params;

    return this.wrapResponse<{ fileId: string; uploadId: string }>({
      promise: this.apiInstance.put(
        `/v2/files/upload/multipart/init/${teamId}/${folderId}`,
        {
          contentType,
        }
      ),
      errorMessage: 'Failed to initialize multipart upload',
    });
  }

  /**
   * Create a part upload URL for a multipart upload
   * @param params - Part upload parameters
   * @returns Promise with upload URL
   */
  async createPartUploadUrl(params: {
    teamId: string;
    folderId: string;
    fileId: string;
    uploadId: string;
    partNumber: number;
    contentType: string;
  }): Promise<IVFSResponse<{ uploadUrl: string }>> {
    const {
      teamId,
      folderId,
      fileId,
      uploadId,
      partNumber,
      contentType,
    } = params;

    return this.wrapResponse<{ uploadUrl: string }>({
      promise: this.apiInstance.put(
        `/v2/files/upload/multipart/url/${teamId}/${folderId}/${fileId}/${uploadId}/${partNumber}`,
        {
          contentType,
        }
      ),
      errorMessage: 'Failed to create part upload URL',
    });
  }

  /**
   * Finalize a multipart upload
   * @param params - Finalize upload parameters
   * @returns Promise with file data
   */
  async finalizeUpload(params: {
    teamId: string;
    folderId?: string;
    fileId: string;
    uploadId: string;
    contentType: string;
    notes: TimeStampedLine[];
    title?: string;
  }): Promise<IVFSResponse<{ file: FileType }>> {
    const {
      teamId,
      folderId,
      fileId,
      uploadId,
      contentType,
      notes,
      title,
    } = params;

    return this.wrapResponse<{ file: FileType }>({
      promise: this.apiInstance.put(
        `/v2/files/upload/multipart/finalize/${teamId}/${folderId}/${fileId}/${uploadId}`,
        {
          file: {
            contentType,
            name:
              title?.trim() ||
              `New Recording ${moment().format('h:mma MMM DD YYYY')}`,
            notes,
          },
        }
      ),
      errorMessage: 'Failed to finalize upload',
    });
  }

  /**
   * Upload a chunk to S3
   * @param params - Upload chunk parameters
   * @returns Promise that resolves when upload is complete
   */
  async uploadChunkToS3(params: {
    uploadUrl: string;
    chunk: Blob;
  }): Promise<void> {
    const { uploadUrl, chunk } = params;

    if (!uploadUrl) {
      throw new Error('No upload URL provided');
    }

    await this.apiInstance.put(uploadUrl, chunk, {
      headers: {
        'Content-Type': chunk.type,
      },
    });
  }
}

export default UploadAPI;
